import { Template, TemplateSortField, TemplateStatus, TemplateStatusType, WorkOrder, WorkOrderStatusType } from "../api/models";
import { useContext, useEffect, useState } from "react";
import { SubmitStatus } from "../api/work-order-api";
import ApiContext from "../api/context";
import Menu from "../views/master/menu";
import ListView from "../components/list-view";
import { Row, Tag } from "antd";
import Title from "antd/es/typography/Title";
import { toDateString, toDateTimeString, toPrintableTemplateStatus, toTemplateStatusColor } from "../constants";
import TemplateComponent, { TemplateFunction } from "../components/template";
import WorkOrderComponent, { WorkOrderFunction } from "../components/work-order";

const MenuKeys = {
  UploadAssets: "Upload Assets",
  CreateWorkOrder: "Create Work Order",
  RenameTemplate: "Rename Template",
  DeleteTemplate: "Delete Template",
  ViewDetails: "View Details"
};

interface MappedTemplate extends Template {
  key?: number,
  displayCreationTime: string,
  displayCurrentStatus: string,
  displayLastStatusTime: string
};

function mapTemplate(template: Template): MappedTemplate {
  const displayCurrentStatus = toPrintableTemplateStatus(template.status!.templateStatusType);
  const displayCreationTime = toDateString(template.creationTime!);
  const displayLastStatusTime = toDateTimeString(template.status?.statusTime);
  return {
    ...template,
    key: template.templateID,
    displayCreationTime,
    displayCurrentStatus,
    displayLastStatusTime
  };
}

function renderTemplateCard(template: MappedTemplate) {
  return (
    <>
      <Row justify="space-between" align="middle">
        <Title level={5} style={{ marginTop: 0, marginBottom: "12px" }}>{template.name}</Title>
        <Tag style={{ marginBottom: "12px" }} color={toTemplateStatusColor(template.status!.templateStatusType)}>{template.displayCurrentStatus}</Tag>
      </Row>
      <Row justify="space-between">
        <span>Created</span>
        <span>{template.displayCreationTime}</span>
      </Row>
      <Row justify="space-between">
        <span>Last Status</span>
        <span>{template.displayLastStatusTime}</span>
      </Row>
    </>
  );
}

export default function () {
  const [templateStatus, setTemplateStatus] = useState<SubmitStatus>(SubmitStatus.Submitting);
  const [templates, setTemplates] = useState<MappedTemplate[]>([]);
  const [templateFunction, setTemplateFunction] = useState<TemplateFunction>(TemplateFunction.None);
  const [template, setTemplate] = useState<MappedTemplate>();
  const [workOrderFunction, setWorkOrderFunction] = useState<WorkOrderFunction>(WorkOrderFunction.None);
  const [workOrder, setWorkOrder] = useState<WorkOrder>();
  const [returnToDetailsOnDeleteCancel, setReturnToDetailsOnDeleteCancel] = useState(false);
  const [renameOpenedFromDetails, setRenameOpenedFromDetails] = useState(false);
  const [sortField, setSortField] = useState<TemplateSortField>(TemplateSortField.CreationTime);
  const [sortDescending, setSortDescending] = useState(false);
  const { api } = useContext(ApiContext);

  useEffect(() => {
    api?.getTemplates({ deleted: false, sortBy: sortField, sortDescending }).then(t => {
      if (!t || !t.body || t.hasError) {
        setTemplateStatus(SubmitStatus.Failed);
        return;
      }

      const mappedTemplates = t.body.map(mapTemplate);
      setTemplates(mappedTemplates);
      setTemplateStatus(SubmitStatus.Submitted);
    });
  }, [sortField, sortDescending]);

  function routeWorkOrder(routedWorkOrder: WorkOrder) {
    setWorkOrder(routedWorkOrder);
    switch (routedWorkOrder.status?.workOrderStatusType) {
      case WorkOrderStatusType.PendingParameters:
        setWorkOrderFunction(WorkOrderFunction.UploadParameters);
        break;
      case WorkOrderStatusType.PendingDynamicAssets:
        setWorkOrderFunction(WorkOrderFunction.UploadAssets);
        break;
      case WorkOrderStatusType.QueuedForProcessing:
      case WorkOrderStatusType.QueuedForRendering:
      case WorkOrderStatusType.Rendering:
      case WorkOrderStatusType.PreparingResults:
        setWorkOrderFunction(WorkOrderFunction.ViewStatuses);
        break;
      case WorkOrderStatusType.Archived:
      case WorkOrderStatusType.Failed:
        setWorkOrderFunction(WorkOrderFunction.None);
        setTemplate(undefined);
        setWorkOrder(undefined);
        break;
      case WorkOrderStatusType.Done:
        setWorkOrderFunction(WorkOrderFunction.ViewResults);
        break;
    }
  }

  return (
    <Menu>
      <ListView
        title="Templates"
        storageKey="templatesViewMode"
        emptyText="There are no templates to show."
        loading={templateStatus === SubmitStatus.Submitting}
        columns={[
          { title: "ID", dataIndex: "templateID", key: "templateID", align: "right", width: 97 },
          { title: "Name", dataIndex: "name", key: "name", align: "left" },
          { title: "Status", width: 150, dataIndex: "displayCurrentStatus", key: "displayCurrentStatus", align: "left" },
          {
            title: "Creation Date",
            width: 120,
            dataIndex: "displayCreationTime",
            key: "displayCreationTime",
            align: "right"
          }
        ]}
        rows={templates}
        renderCard={renderTemplateCard}
        sortOptions={[
          { label: "Creation Time", value: TemplateSortField.CreationTime },
          { label: "Name", value: TemplateSortField.Name },
          { label: "Last Status Time", value: TemplateSortField.LastStatusTime },
          { label: "Last Status", value: TemplateSortField.LastStatus }
        ]}
        sortField={sortField}
        onSortFieldChange={value => setSortField(value as TemplateSortField)}
        sortDescending={sortDescending}
        onSortDescendingChange={setSortDescending}
        onSelect={e => {
          const selectedTemplate = templates.find(t => t.templateID! === e.templateID!);
          setTemplate(selectedTemplate);
          if (selectedTemplate?.status?.templateStatusType === TemplateStatusType.PendingAssets) {
            setTemplateFunction(TemplateFunction.Upload);
            return;
          }

          setTemplateFunction(TemplateFunction.Details);
        }}
        onContextMenu={r => {
          const options = [];
          const isPendingAssets = r.status?.templateStatusType === TemplateStatusType.PendingAssets;
          if (isPendingAssets) {
            options.push(MenuKeys.UploadAssets);
          } else {
            // TODO: Uncomment this when the work order component is ready.
            options.push(MenuKeys.CreateWorkOrder);
          }

          options.push(MenuKeys.RenameTemplate);
          options.push(MenuKeys.DeleteTemplate);
          options.push(MenuKeys.ViewDetails);
          return options;
        }}
        onContextMenuOptionSelected={(r, option) => {
          setTemplate(r);
          switch (option) {
            case MenuKeys.UploadAssets:
              setTemplateFunction(TemplateFunction.Upload);
              break;
            case MenuKeys.RenameTemplate:
              setRenameOpenedFromDetails(false);
              setTemplateFunction(TemplateFunction.Rename);
              break;
            case MenuKeys.CreateWorkOrder:
              setWorkOrderFunction(WorkOrderFunction.Create);
              break;
            case MenuKeys.DeleteTemplate:
              setReturnToDetailsOnDeleteCancel(false);
              setTemplateFunction(TemplateFunction.Delete);
              break;
            case MenuKeys.ViewDetails:
              setTemplateFunction(TemplateFunction.Details);
              break;
          }
        }}
        onAction={() => {
          setTemplateFunction(TemplateFunction.Create)
        }}
      />
      <TemplateComponent
        template={template}
        templateFunction={templateFunction}
        onClose={() => {
          setTemplateFunction(TemplateFunction.None);
          setTemplate(undefined);
        }}
        onDelete={deletedTemplate => {
          const filteredTemplates = templates.filter(t => t.templateID !== deletedTemplate.templateID);
          setTemplates(filteredTemplates);
          setTemplateFunction(TemplateFunction.None);
          setTemplate(undefined);
        }}
        onSave={savedTemplate => {
          const mappedTemplate = mapTemplate(savedTemplate);
          const shouldReturnToDetails = renameOpenedFromDetails;
          setRenameOpenedFromDetails(false);
          setTemplate(mappedTemplate);
          setTemplates(previous => previous.map(t => t.templateID === mappedTemplate.templateID ? mappedTemplate : t));
          if (mappedTemplate.status?.templateStatusType === TemplateStatusType.PendingAssets) {
            setTemplateFunction(TemplateFunction.Upload);
            return;
          }

          setTemplateFunction(shouldReturnToDetails ? TemplateFunction.Details : TemplateFunction.None);
        }}
        onCreateWorkOrder={() => {
          setTemplateFunction(TemplateFunction.None);
          setWorkOrderFunction(WorkOrderFunction.Create);
        }}
        onDeleteRequested={() => {
          setReturnToDetailsOnDeleteCancel(true);
          setTemplateFunction(TemplateFunction.Delete);
        }}
        onDeleteClose={() => {
          if (returnToDetailsOnDeleteCancel) {
            setReturnToDetailsOnDeleteCancel(false);
            setTemplateFunction(TemplateFunction.Details);
            return;
          }

          setTemplateFunction(TemplateFunction.None);
          setTemplate(undefined);
        }}
        onRenameRequested={() => {
          setRenameOpenedFromDetails(true);
          setTemplateFunction(TemplateFunction.Rename);
        }}
        onRenameClose={() => {
          const shouldReturnToDetails = renameOpenedFromDetails;
          setRenameOpenedFromDetails(false);
          if (shouldReturnToDetails) {
            setTemplateFunction(TemplateFunction.Details);
            return;
          }

          setTemplateFunction(TemplateFunction.None);
          setTemplate(undefined);
        }}
        onConfirmed={confirmedTemplate => {
          const mappedTemplate = mapTemplate(confirmedTemplate);
          setTemplate(mappedTemplate);
          setTemplates(previous => previous.map(t => t.templateID === mappedTemplate.templateID ? mappedTemplate : t));
          setTemplateFunction(TemplateFunction.Details);
        }}
        onCreate={createdTemplate => {
          const mappedTemplate = mapTemplate(createdTemplate);
          setTemplate(mappedTemplate);

          api?.getTemplates({ deleted: false, sortBy: sortField, sortDescending }).then(t => {
            if (!t || !t.body || t.hasError) {
              return;
            }
            setTemplates(t.body.map(mapTemplate));
          });

          if (mappedTemplate.status?.templateStatusType === TemplateStatusType.PendingAssets) {
            setTemplateFunction(TemplateFunction.Upload);
            return;
          }

          setTemplateFunction(TemplateFunction.None);
        }}
      />
      <WorkOrderComponent
        workOrderFunction={workOrderFunction}
        template={template}
        workOrder={workOrder}
        onClose={() => {
          setWorkOrderFunction(WorkOrderFunction.None);
          setTemplate(undefined);
          setWorkOrder(undefined);
        }}
        onSave={routeWorkOrder}
        onCreate={routeWorkOrder}
      />
    </Menu>
  );
}